/* Reveal-on-scroll layer promotion fix, 2026-08-19. WITHDRAWN AS A
   PERFORMANCE FINDING, 2026-08-28 - see the correction below. Kept live
   as a correctness fix only.

   Original claim, now withdrawn: CDP trace showed 5235 Paint events over
   two scroll passes on the wallet PDP with recently-viewed-products
   populated, and disabling reveal-on-scroll dropped that by over 99
   percent - taken as evidence that theme.js's ProductList.reveal()
   (js/common/product/product-list.js) leaves revealed cards permanently
   promoted to their own compositor layer, with every later repaint having
   to composite all of them. That measurement does not reproduce. A clean,
   same-session retest with correct stepped scrolling (see the 2026-08-28
   correction below) found unfixed, fixed, and reveal-on-scroll-disabled
   conditions all within half a percent of each other (~3975-3996 Paint
   events). The original 5235/>99% numbers were almost certainly produced
   by the same instant/large-jump scroll bug documented below - Motion
   One's staggered animation this mechanism depends on does not reliably
   fire under that scroll pattern, so a "before" measurement that never
   triggered the real animation and an "after" measurement that also never
   triggered it will look like a huge improvement without one being real.
   Stage 2 is closed as refuted, not as fixed - reveal-on-scroll layer
   promotion was never shown to be a real cost on this page once measured
   correctly.

   Standing instruction: do not patch ProductList, do not touch theme.js.
   This file works entirely independently of both - a separate
   IntersectionObserver on the same <product-list> elements ProductList's
   own reveal() already watches, priming `will-change: transform` on their
   reveal-on-scroll children right before the animation would start, then
   releasing it back to `auto` the moment each element's own real
   animation (read via the native Web Animations API, getAnimations())
   finishes. Never reads or calls anything on ProductList itself.

   Scoped to both sections named in the finding: related-products and
   recently-viewed-products. Both render real <product-list> elements
   inside their own `.shopify-section--*` wrapper (confirmed by reading
   both section files) - recently-viewed-products renders two
   (a grid layout and a carousel wrapper), both included.

   Correction, 2026-08-28: the first shipped version used a fixed 20-frame
   (~333ms) timeout before falling back to releasing will-change, on the
   assumption that was generous headroom over the animation. It measured
   3996 Paint events over two stepped-scroll passes against a 5235 unfixed
   baseline and a >99% drop when reveal-on-scroll was disabled entirely -
   only a 24% reduction, nowhere near the target, so the fix was not
   working. Root cause: theme.js's ProductList.reveal() calls Motion One's
   animate() with `delay: stagger(0.05, { start: 0.4, ... })` on this exact
   element set (js/common/product/product-list.js) - the FIRST element in
   a revealed list doesn't start animating until 400ms after reveal()
   fires, before any stagger is even added, and later elements start even
   later. A 333ms timeout is shorter than that base delay alone, so
   getAnimations() found nothing for every element, every time, and
   releaseWhenAnimated's fallback branch fired before any real animation
   ever attached - will-change was being released before the layer
   promotion it exists to manage even began. Fixed by computing the wait
   from the same stagger formula theme.js itself uses, per list (so it
   scales correctly with however many cards are actually in a given
   related-products/recently-viewed-products list), instead of one fixed
   guess.

   Why this file is still kept live despite the withdrawal above: the
   timeout bug it fixes is real and independently confirmed by reading
   theme.js's own animate() call, not by the withdrawn Paint measurement -
   will-change should track the actual animation lifecycle rather than
   release before the animation exists, on principle, regardless of
   whether the underlying layer-promotion cost this file was built to
   address turned out to be real on this page. It is a correctness fix
   standing on its own, not a performance fix that failed to deliver.
*/
(function () {
  var SECTION_SCOPE = '.shopify-section--related-products, .shopify-section--recently-viewed-products';
  var REVEAL_CHILD_SELECTOR = 'product-card[reveal-on-scroll="true"], .product-list__promo[reveal-on-scroll="true"]';
  // Mirrors theme.js's own animate9(revealElements, {...}, {duration: 0.2,
  // delay: stagger(0.05, {start: 0.4, ...})}) call exactly (js/common/
  // product/product-list.js, ProductList.reveal()) - these four constants
  // are Motion One's own values for this exact call, not independent
  // guesses, and must be kept in sync with that call if it ever changes.
  var STAGGER_START_S = 0.4;
  var STAGGER_STEP_S = 0.05;
  var ANIMATION_DURATION_S = 0.2;
  var SAFETY_MARGIN_S = 0.3; // headroom for scheduling jitter and slow frames
  var FRAME_MS = 1000 / 60;
  var primed = new WeakSet();

  function nextFrame() {
    return new Promise(function (resolve) { requestAnimationFrame(resolve); });
  }

  async function releaseWhenAnimated(el, maxFrames) {
    for (var i = 0; i < maxFrames; i++) {
      var anims = el.getAnimations();
      if (anims.length > 0) {
        try {
          await Promise.all(anims.map(function (a) { return a.finished; }));
        } catch (e) {
          // an animation was canceled rather than completing - still release below
        }
        el.style.willChange = 'auto';
        return;
      }
      await nextFrame();
    }
    // No animation ever attached within the wait window (e.g. reduced-motion
    // toggled mid-flight, or the element was never actually revealed) -
    // release on a hard fallback so will-change never lingers indefinitely.
    el.style.willChange = 'auto';
  }

  function primeProductList(productListEl) {
    var children = productListEl.querySelectorAll(REVEAL_CHILD_SELECTOR);
    var count = children.length;
    // Worst case is the LAST element in stagger order, which theme.js
    // itself doesn't start animating until STAGGER_START_S +
    // STAGGER_STEP_S * (count - 1) seconds after reveal() fires.
    var maxWaitMs = (STAGGER_START_S + STAGGER_STEP_S * Math.max(0, count - 1) + ANIMATION_DURATION_S + SAFETY_MARGIN_S) * 1000;
    var maxWaitFrames = Math.ceil(maxWaitMs / FRAME_MS);
    children.forEach(function (el) {
      if (primed.has(el)) return;
      primed.add(el);
      el.style.willChange = 'transform';
      releaseWhenAnimated(el, maxWaitFrames);
    });
  }

  var listObserver = new IntersectionObserver(function (entries, observer) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      primeProductList(entry.target);
      observer.unobserve(entry.target);
    });
  });

  function observeProductLists(root) {
    (root || document).querySelectorAll(SECTION_SCOPE + ' product-list').forEach(function (el) {
      listObserver.observe(el);
    });
  }

  function boot() {
    observeProductLists();
    // related-products and recently-viewed-products can both render their
    // content after initial page load (recently-viewed-products reads
    // localStorage/session data client-side) - watch for their
    // <product-list> elements arriving late.
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches(SECTION_SCOPE)) observeProductLists(node);
          else if (node.closest && node.closest(SECTION_SCOPE)) observeProductLists(node.closest(SECTION_SCOPE));
          else if (node.querySelectorAll) observeProductLists(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
